<template>
  <div class="menu-input-group">
    <label class="block my-3">
      {{ t('templateBuilder.fields.elementsConfiguration', null) }}
    </label>
    <div class="elements-configuration">
      <draggable
        v-model="menuItems"
        item-key="key"
        handle=".drag-icon"
        @change="handleOrderChange"
      >
        <template #item="{ element, index }">
          <div class="menu-item mb-4">
            <div class="menu-item-header">
              <i
                class="fas fa-arrows-alt-v mr-2 drag-icon"
                :title="t('templateBuilder.fields.dragToReorder')"
              />
              <span class="item-label">{{ element.label || t('templateBuilder.fields.menuItem') }}</span>
              <Button
                icon="far fa-trash"
                class="p-button-text p-button-danger delete-button"
                :title="t('templateBuilder.fields.delete')"
                @click="handleRemoveItem(index)"
              />
            </div>
            <div class="menu-item-content">
              <div class="field-row">
                <label class="block mb-2">
                  {{ t('templateBuilder.fields.menuItemLabel') }}
                </label>
                <InputText
                  v-model="element.label"
                  class="w-full"
                  :placeholder="t('templateBuilder.fields.menuItemLabel')"
                  @input="handleLabelChange(index)"
                />
              </div>
              <div class="field-row mt-3">
                <label class="block mb-2">
                  {{ t('templateBuilder.fields.menuItemUrl') }}
                </label>
                <InputText
                  v-model="element.url"
                  class="w-full"
                  :placeholder="t('templateBuilder.fields.enterUrl')"
                  @input="handleUrlChange(index)"
                />
              </div>
            </div>
          </div>
        </template>
      </draggable>
      <div class="add-item-section">
        <Button
          :label="t('templateBuilder.fields.addElement')"
          icon="far fa-plus"
          class="p-button-outlined w-full"
          @click="handleAddItem"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, ref, watch,
} from 'vue';
import draggable from 'vuedraggable';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';
import { useI18n } from 'vue-i18n';

interface MenuItem {
  key: string;
  label: string;
  url: string;
}

export default defineComponent({
  name: 'MenuInputGroup',

  components: {
    draggable,
    InputText,
    Button,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },
    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);

    const menuItems = ref<MenuItem[]>([]);

    const initializeMenuItems = () => {
      const items = property.value.value || [];
      menuItems.value = items.map((item: any) => ({
        key: item.key || `spm_menu_item_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
        label: item.label || '',
        url: item.url || '',
      }));
    };

    const generateUniqueKey = (): string => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      return `spm_menu_item_${timestamp}_${random}`;
    };

    const emitChanges = () => {
      const formattedValue = menuItems.value.map((item) => ({
        key: item.key,
        label: item.label,
        url: item.url,
      }));

      property.value.value = formattedValue;
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
    };

    const handleAddItem = () => {
      const newItem: MenuItem = {
        key: generateUniqueKey(),
        label: `Menu ${menuItems.value.length + 1}`,
        url: '{var=shop.url}',
      };
      menuItems.value.push(newItem);
      emitChanges();
    };

    const handleRemoveItem = (index: number) => {
      menuItems.value.splice(index, 1);
      emitChanges();
    };

    const handleLabelChange = (index: number) => {
      emitChanges();
    };

    const handleUrlChange = (index: number) => {
      emitChanges();
    };

    const handleOrderChange = () => {
      emitChanges();
    };

    watch(() => props.parserValues.properties[0], () => {
      property.value = props.parserValues.properties[0];
      initializeMenuItems();
    }, { immediate: true, deep: true });

    return {
      t,
      menuItems,
      handleAddItem,
      handleRemoveItem,
      handleLabelChange,
      handleUrlChange,
      handleOrderChange,
    };
  },
});
</script>

<style scoped lang="scss">
.menu-input-group {
  .elements-configuration {
    padding: 15px;
    border: solid 1px $solitude;
    border-radius: 3px;
  }

  .menu-item {
    background: $alice-blue;
    border: 1px solid $solitude;
    border-radius: 4px;
    padding: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .menu-item-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid $solitude;

    .drag-icon {
      color: $heather;
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }

    .item-label {
      flex: 1;
      font-weight: 500;
    }

    .delete-button {
      padding: 4px 8px;
    }
  }

  .menu-item-content {
    .field-row {
      label {
        font-size: 0.9rem;
        color: $shuttle-grey;
      }
    }
  }

  .add-item-section {
    margin-top: 16px;
  }
}
</style>
